import React, { useState } from "react";
import classNames from "classnames";
import { withRouter, Link } from "react-router-dom";
import queryString from "query-string";
import { formatApiDateTime } from "../../tools/formatApiDateTime";
import { unstable_useMediaQuery as useMediaQuery } from "@material-ui/core/useMediaQuery";
import { withStyles, withTheme } from "@material-ui/core/styles";
import { Helmet } from "react-helmet";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import SettingsIcon from "@material-ui/icons/Settings";
import withContext from "../../tools/withContext";
import EnhancedTableHeader from "../common/EnhancedTableHeader";
import TableBody from "../common/TableBody";
import TableNavigation from "../common/TableNavigation";
import ProjectActionButton from "./components/ProjectActionButton";
import ProjectsActionButton from "./components/ProjectsActionButton";
import UiSearchInputField from "../common/UiSearchInputField";
import ProjectType from "../../types/Project";
import ContextType from "../../types/Context";
import DimensionsContainer from "../common/DimensionsContainer";
import useRouter from "../../tools/useRouter";
import { tablePageStyles, tablePageRootStyles, tablePageToolbarStyles } from "../common/tableTheme";

const columns = [
  {
    id: "name",
    numeric: false,
    sortable: true,
    disablePadding: true,
    label: "Nom du projet",
    onlyRoles: null,
    size: 2.5,
    align: "left",
  },
  {
    id: "createdDate",
    numeric: false,
    sortable: true,
    disablePadding: false,
    label: "Date de création",
    onlyRoles: ["ROLE_ADMIN", "ROLE_PROJECT_ADMIN"],
    size: 2.5,
    align: "center",
  },
  {
    id: "labelingType",
    numeric: false,
    sortable: true,
    disablePadding: false,
    label: "Type de labellisation",
    onlyRoles: null,
    size: 2.5,
    align: "center",
  },
  {
    id: "dataSource",
    numeric: false,
    sortable: false,
    disablePadding: false,
    label: "Source de données",
    onlyRoles: null,
    size: 2,
    align: "center",
  },
  {
    id: "clientStr",
    numeric: false,
    sortable: true,
    disablePadding: false,
    label: "Client",
    onlyRoles: null,
    size: 2,
    align: "center",
  },
  {
    id: "secondsToFinish",
    numeric: true,
    sortable: false,
    disablePadding: false,
    label: "Lots",
    onlyRoles: null,
    size: 2.5,
    align: "center",
  },
  {
    id: "actions",
    numeric: false,
    sortable: false,
    disablePadding: false,
    label: "Actions",
    onlyRoles: null,
    size: 1.5,
    align: "right",
  },
];

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...tablePageRootStyles(theme),
    },
    ...tablePageStyles(theme),
    ...tablePageToolbarStyles(theme),
    fullWidth: {
      width: "100%",
    },
  });

interface PropsType {
  classes: any;
  ctx: ContextType;
  projects: ProjectType[];
  progress: any[];
  deleteProject: Function;
  isLoading: boolean;
  onlyProcessingProjects: boolean;
  theme: any;
  pagination: any;
  history: any;
  match: any;
  appFetch: any;
  searchTerm: string;
  setSearchTerm: Function;
}

const Projects = (props: PropsType) => {
  const {
    classes,
    ctx: { user },
    projects,
    isLoading,
    pagination,
    match: {
      params: { noPage },
    },
    theme,
  } = props;
  const matches = useMediaQuery(theme.breakpoints.down("lg"));
  const router = useRouter();
  const searchParsed = router.query;
  const s = searchParsed.searchTerm || "";

  const [order, setOrder] = useState(
    //@ts-ignore
    searchParsed.sort ? searchParsed.sort.split(",")[1] : "desc"
  );
  const [orderBy, setOrderBy] = useState(
    //@ts-ignore
    searchParsed.sort ? searchParsed.sort.split(",")[0] : "name"
  );
  //@ts-ignore
  const [searchTerm, setSearchTerm] = useState<string>(s);
  const [selected, setSelected] = useState<string[]>([]);

  const handleRequestSort = (event: any, property: string) => {
    let _orderBy = property;
    let _order = "desc";

    if (orderBy === property && order === "desc") {
      _order = "asc";
    }

    props.history.push(
      `/projects/${noPage}?${queryString.stringify(
        { ...searchParsed, sort: `${_orderBy},${_order}` },
        { strict: false }
      )}`
    );
    setOrderBy(_orderBy);
    setOrder(_order);
  };

  const handleSelection = (event: any, batchSelectedId: any) => {
    const selectedIndex = selected.indexOf(batchSelectedId);
    let newSelected: string[] = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, batchSelectedId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isAdmin = user && user.authorities[0] === "ROLE_ADMIN";

  const buildColumns = (project: ProjectType) => {
    const createdDate = project.createdDate || project.creationDate;

    return [
      <div style={{ padding: "10px" }}>{project.name}</div>,
      <div>{formatApiDateTime(createdDate)}</div>,
      <div>{project.labelingType}</div>,
      <div>{project.dataSourceStr}</div>,
      <div>{project.clientStr}</div>,
      <div>
        {project.totalBatches > 0 && (
          <Link to={`/batches/0?projectId=${project.id}&sort=createdDate,desc`}>
            Afficher les lots
          </Link>
        )}
      </div>,
      <div>
        <ProjectActionButton
          project={project}
          isAdmin={isAdmin}
          variant="modern"
        />
      </div>,
    ];
  };

  const handleChangePage = (noP: any) => {
    props.history.push(
      `/projects/${noP}?${queryString.stringify(router.query)}`
    );
  };

  const handleSearch = (str: string) => {
    const trimmed = str.trim();
    setSearchTerm(trimmed);
    props.history.push(
      `/projects/${trimmed.length > 0 ? "0" : noPage}?${queryString.stringify({
        ...router.query,
        searchTerm: trimmed.length > 0 ? trimmed : undefined,
      })}`
    );
  };

  return (
    <Grid container direction="column" className={classes.root}>
      <Helmet>
        <title>Liste des projets</title>
      </Helmet>
      <Grid item className={classes.fullWidth}>
        <div className={classes.pageToolbar}>
          <div className={classes.pageToolbarLeft}>
            <div className={classes.pageTitleRow}>
              <Typography
                component="h2"
                className={classNames(classes.pageTitle, {
                  [classes.pageTitleCompact]: matches,
                })}
              >
                Liste des projets
              </Typography>
              <Link
                title="Afficher les opérations récentes"
                to="/projectsOperations"
                className={classes.pageTitleIconLink}
              >
                <SettingsIcon color="secondary" fontSize="small" />
              </Link>
            </div>
          </div>
          <div className={classes.pageToolbarRight}>
            <div className={classes.pageLoaderSlot}>
              {isLoading && (
                <CircularProgress
                  color="secondary"
                  size={20}
                  className={classes.pageLoader}
                />
              )}
            </div>
            {isAdmin && <ProjectsActionButton toolbarVariant="modern" />}
            <div className={classes.searchArea}>
              <div className={classes.searchGroup}>
                <div className={classes.searchFieldInGroupOnly}>
                  <UiSearchInputField
                    searchString={searchTerm}
                    setSearchString={handleSearch}
                    variant="modern"
                    inGroup
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Grid>
      <Grid item className={classes.tableWrapper}>
        <Paper elevation={0} className={classes.tableCard}>
          <EnhancedTableHeader
            variant="modern"
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={projects.length}
            columns={columns}
            withCheck={false}
            numSelected={selected.length}
            actionsCellSize={3}
          />
          {projects.length === 0 && !isLoading && (
            <Typography className={classes.tableEmptyInCard} variant="h6">
              Pour l'instant, aucun projet trouvé
            </Typography>
          )}
          {projects.length > 0 && (
            <div className={classes.tableScrollArea}>
              <DimensionsContainer variant="modern">
                <TableBody
                  variant="modern"
                  handleSelection={handleSelection}
                  selected={selected}
                  buildColumns={buildColumns}
                  datas={projects}
                  columns={columns}
                  noPage={noPage}
                />
              </DimensionsContainer>
            </div>
          )}
          {!isLoading && pagination && pagination.totalElements > 0 && (
            <TableNavigation
              variant="modern"
              pagination={pagination}
              onChangePage={handleChangePage}
              itemName="Projets"
            />
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(
  withRouter(withContext(withTheme()(Projects)))
);
